import { useEffect, useMemo, useState } from 'react';
import {
  Assessment,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import ActiveTenantReportAlert from './ActiveTenantReportAlert';
import {
  createUserActivityReport,
  extractActiveTenantReportFromError,
  extractApiDetailMessage,
  fetchActiveTenantReport,
} from '../../services/reports';

import type { ActiveTenantReportSummary, UserActivityReportListItem } from '../../types';


export interface UserActivityReportSummaryEntry {
  key: string;
  label: string;
  values: string[];
  warning?: string | null;
}

interface CreateUserActivityReportDialogProps {
  open: boolean;
  onClose: () => void;
  filtersPayload: Record<string, string>;
  filterEntries: UserActivityReportSummaryEntry[];
  defaultName: string;
  defaultDescription: string;
  canConfirm: boolean;
  validationMessage?: string | null;
  onCreated: (report: UserActivityReportListItem) => void;
}

function getErrorMessage(error: unknown): string {
  if (
    typeof error === 'object'
    && error !== null
    && 'response' in error
    && typeof error.response === 'object'
    && error.response !== null
    && 'data' in error.response
  ) {
    const responseData = error.response.data as Record<string, unknown>;
    const filtersPayload = (responseData.filters_payload || responseData.filtersPayload) as Record<string, unknown> | undefined;
    const dateRangeMessage = filtersPayload?.date_range || filtersPayload?.dateRange;
    if (typeof dateRangeMessage === 'string') {
      return dateRangeMessage;
    }
  }
  return extractApiDetailMessage(error) || '';
}

export default function CreateUserActivityReportDialog({
  open,
  onClose,
  filtersPayload,
  filterEntries,
  defaultName,
  defaultDescription,
  canConfirm,
  validationMessage,
  onCreated,
}: CreateUserActivityReportDialogProps) {
  const { t, i18n } = useTranslation(['pages', 'common']);
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState(defaultDescription);
  const [submitting, setSubmitting] = useState(false);
  const [loadingActiveTenantReport, setLoadingActiveTenantReport] = useState(false);
  const [activeTenantReport, setActiveTenantReport] = useState<ActiveTenantReportSummary | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setName(defaultName);
    setDescription(defaultDescription);
    setSubmitting(false);
    setLoadingActiveTenantReport(true);
    setActiveTenantReport(null);
    setErrorMessage(null);

    let mounted = true;
    fetchActiveTenantReport()
      .then((report) => {
        if (!mounted) {
          return;
        }
        setActiveTenantReport(report);
      })
      .catch(() => {
        if (!mounted) {
          return;
        }
        setActiveTenantReport(null);
      })
      .finally(() => {
        if (!mounted) {
          return;
        }
        setLoadingActiveTenantReport(false);
      });

    return () => {
      mounted = false;
    };
  }, [defaultDescription, defaultName, open]);

  const sortedEntries = useMemo(
    () => filterEntries.filter((entry) => entry.values.length > 0),
    [filterEntries],
  );

  const confirmDisabled = !canConfirm || submitting || loadingActiveTenantReport || Boolean(activeTenantReport);

  const handleConfirm = async () => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const report = await createUserActivityReport({
        name: name.trim(),
        description: description.trim(),
        filtersPayload,
      });
      onCreated(report);
    } catch (error) {
      const lockedReport = extractActiveTenantReportFromError(error);
      if (lockedReport) {
        setActiveTenantReport(lockedReport);
        setErrorMessage(null);
        setSubmitting(false);
        return;
      }
      setErrorMessage(getErrorMessage(error) || t('pages:reports.users.create.submitError'));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Assessment sx={{ color: 'primary.main' }} />
          <Box component="span">{t('pages:reports.users.create.title')}</Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t('pages:reports.users.create.subtitle')}
          </Typography>

          {activeTenantReport && (
            <ActiveTenantReportAlert report={activeTenantReport} locale={i18n.language} />
          )}

          {validationMessage && (
            <Alert severity="warning">
              {validationMessage}
            </Alert>
          )}

          {errorMessage && (
            <Alert severity="error">
              {errorMessage}
            </Alert>
          )}

          <TextField
            label={t('pages:reports.users.create.nameLabel')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label={t('pages:reports.users.create.descriptionLabel')}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={3}
          />

          <Divider />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="subtitle2">
              {t('pages:reports.users.create.filtersTitle')}
            </Typography>
            {sortedEntries.map((entry) => (
              <Box key={entry.key} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  {entry.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {entry.values.join(' • ')}
                </Typography>
                {entry.warning && (
                  <Typography variant="caption" color="error.main">
                    {entry.warning}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={confirmDisabled}
          startIcon={submitting ? <CircularProgress color="inherit" size={16} /> : <Assessment />}
        >
          {t('pages:reports.users.create.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
